/**
 * Keep behavior aligned with src/utils/aiTagSuggestedFix.ts.
 * Slug shape only here; API PATCH accepts vocabulary + brand slugs and strips noise (e.g. "other").
 */
const TAG_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function isPlausibleTagSlug(s: string): boolean {
  const t = s.trim().toLowerCase();
  if (!t || t.length > 48) return false;
  return TAG_SLUG_PATTERN.test(t);
}

/** Same rule as src/utils/proOnlyProduct.ts: a model must not assign the channel-restriction tag. */
function withoutProOnlyTag(tags: string[]): string[] {
  return tags.filter((tag) => tag.trim().toLowerCase() !== 'pro-only');
}

export function buildTagsFromAiSuggestedFix(current: string[] | undefined, suggestedFix: unknown): string[] | null {
  const cur = (current || []).map((x) => String(x || '').trim()).filter(Boolean);

  if (suggestedFix == null) return null;

  if (Array.isArray(suggestedFix)) {
    const next = suggestedFix
      .filter((x): x is string => typeof x === 'string')
      .map((s) => s.trim().toLowerCase())
      .filter(isPlausibleTagSlug);
    const kept = withoutProOnlyTag(next);
    if (kept.length === 0) return null;
    if (cur.some((tag) => tag.toLowerCase() === 'pro-only') && !kept.some((tag) => tag.toLowerCase() === 'pro-only')) {
      kept.push('pro-only');
    }
    return Array.from(new Set(kept));
  }

  if (typeof suggestedFix === 'string') {
    const t = suggestedFix.trim();
    if (!t) return null;

    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t) as unknown;
        if (Array.isArray(p)) return buildTagsFromAiSuggestedFix(cur, p);
      } catch {
        return null;
      }
    }

    const removeMatch =
      t.match(/remove\s+['"]?([\w-]+)['"]?\s+from\s+tags?/i) ||
      t.match(/remove\s+tag\s+['"]?([\w-]+)['"]?/i);
    if (removeMatch) {
      const slug = removeMatch[1].toLowerCase();
      if (slug === 'pro-only') return null;
      return cur.filter((tag) => String(tag).toLowerCase() !== slug);
    }

    if (isPlausibleTagSlug(t) && !/\s/.test(t)) {
      if (t.toLowerCase() === 'pro-only') return null;
      return Array.from(new Set([...cur, t.toLowerCase()]));
    }

    return null;
  }

  return null;
}
